import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import ConfirmDialog from "components/ConfirmDialog";
import Field from "components/Field";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useState } from "react";
import { Form } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  ListResponseOfOrganizationAdminModel,
  OrganizationAdminConflictsRequest,
  OrganizationAdminModel,
  OrganizationAdminRequest,
  OrganizationDetails,
  ResponseCode,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import { useNavigate } from "react-router-dom";
import OrganizationAdminActions from "./OrganizationAdminActions";

type OrganizationAdminUsersProps = {
  organization: OrganizationDetails;
};

type OrganizationAdminUserFormValues = {
  firstName: string;
  lastName: string;
  email: string;
};

const useStyles = (theme: Theme) => ({
  adminHeader: {
    display: "flex",
    justifyContent: "end",
    margin: theme.spacing(4, 0, 3, 0),
    width: "100%",
  },
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  descendantAdminsWarning: {
    marginTop: theme.spacing(2),
  },
});

const OrganizationAdminUsers = ({
  organization,
}: OrganizationAdminUsersProps) => {
  const { formatMessage: t } = useIntl();
  const theme = useTheme();
  const styles = useStyles(theme);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const { organizationService, userDetailsUrl } = useGlobalContext();
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("email");
  const [page, setPage] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [adminIdToDelete, setAdminIdToDelete] = useState<string>();
  const [addConfirmOpen, addConfirmOpenActions] = useBoolean(false);
  const [descendantAdmins, setDescendantAdmins] = useState<
    OrganizationAdminModel[]
  >([]);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [deleteAdminConfirmOpen, deleteAdminConfirmOpenActions] = useBoolean(
    false
  );

  const [
    formValues,
    setFormValues,
  ] = useState<OrganizationAdminUserFormValues | null>(null);
  const navigate = useNavigate();

  const deleteOrganizationAdmin = async () => {
    await organizationService.deleteAdminUser(adminIdToDelete!);
    queryClient.invalidateQueries("administrators");
    deleteAdminConfirmOpenActions.setFalse();
    setAdminIdToDelete(undefined);
  };

  const administrators = useStrictQuery<ListResponseOfOrganizationAdminModel>(
    ["administrators", organization.id, page, rowsPerPage, orderBy, order],
    () =>
      organizationService.getAdminUsers(
        organization.id,
        AdminListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  const adminUsers = (administrators.data?.data ?? []).map(
    (a) =>
      ({
        ...a,
        color:
          a.organizationId !== organization.id
            ? theme.palette.grey[200]
            : undefined,
        tooltip:
          a.organizationId !== organization.id
            ? t(
                { id: "adminIsInherited" },
                { organizationName: a.organizationName }
              )
            : undefined,
      } as OrganizationAdminModel & LazyTableRow)
  );

  const AdminActions = ({ value }: { value: OrganizationAdminModel }) => {
    return (
      <OrganizationAdminActions
        onView={() => navigate(userDetailsUrl(value.id))}
        onDelete={
          value.organizationId !== organization.id
            ? undefined
            : () => {
                setAdminIdToDelete(value.id);
                deleteAdminConfirmOpenActions.setTrue();
              }
        }
      />
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "firstName",
      type: "text",
      label: t({ id: "firstNameLabel" }),
    },
    {
      accessor: "lastName",
      type: "text",
      label: t({ id: "lastNameLabel" }),
    },
    {
      accessor: "email",
      customSortAccessor: "userAccount.email",
      type: "text",
      label: t({ id: "emailLabel" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      type: "text",
      label: t({ id: "inheritedFrom" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: AdminActions,
      noSort: true,
      label: t({ id: "actions" }),
    },
  ];

  const onSubmit = async (values: OrganizationAdminUserFormValues) => {
    setSubmitError(null);
    try {
      const result = await organizationService.getAdminConflicts(
        OrganizationAdminConflictsRequest.fromJS({
          email: values.email,
          organizationId: organization.id,
        })
      );

      if (result.responseCode !== ResponseCode.OK) {
        setSubmitError(result.responseReason ?? null);
        return;
      }

      const inheritedAdmin = result.data?.inheritedAdmin;
      if (inheritedAdmin) {
        setSubmitError(
          t(
            {
              id:
                inheritedAdmin.organizationId === organization.id
                  ? "adminAlreadyInThisOrganization"
                  : "adminInheritedFromOrganization",
            },
            {
              email: values.email,
              organizationName: inheritedAdmin.organizationName,
            }
          )
        );
        return;
      }

      setDescendantAdmins(result.data?.descendantAdmins ?? []);
      setFormValues(values);
      addConfirmOpenActions.setTrue();
    } catch (error) {
      setSubmitError(translateError(error, t));
    }
  };

  const onConfirm = async () => {
    try {
      await saveAdministrator(formValues!);
    } catch (err: any) {
      setSubmitError(translateError(err, t));
    } finally {
      setFormValues(null);
      setDescendantAdmins([]);
      addConfirmOpenActions.setFalse();
    }
  };

  const saveAdministrator = async (values: OrganizationAdminUserFormValues) => {
    try {
      setSubmitError(null);
      const result = await organizationService.saveAdminUser(
        OrganizationAdminRequest.fromJS({
          ...values,
          organizationId: organization.id,
          deactivateDescendantAdmins: descendantAdmins.length > 0,
        })
      );

      if (result.responseCode !== ResponseCode.OK) {
        setSubmitError(result.responseReason ?? null);
        return;
      }

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "administratorSaveSuccess" }))
      );
      queryClient.invalidateQueries("administrators");
      queryClient.invalidateQueries("searchOrganizationUsers");
      createOpenActions.setFalse();
    } catch (error) {
      setSubmitError(translateError(error, t));
    }
  };

  return (
    <Box>
      <LazyTable
        {...{
          rowCount: administrators.data?.totalRecords ?? 0,
          rows: adminUsers,
          loading: administrators.isFetching,
          titleComponent: (
            <Box sx={styles.adminHeader}>
              <Button
                onClick={createOpenActions.setTrue}
                startIcon={<AddIcon />}
              >
                <T id="addAdministrator" />
              </Button>
            </Box>
          ),
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />
      <Dialog
        open={createOpen}
        disableEnforceFocus
        maxWidth="sm"
        fullWidth
        onClose={createOpenActions.setFalse}
      >
        <Form
          onSubmit={onSubmit}
          render={({ handleSubmit, submitting, values }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Field name="firstName" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "firstNameLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="lastName" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "lastNameLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="email" required isEmail>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          type="email"
                          label={t({ id: "emailLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>
                </Grid>
                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </DialogContent>

              <DialogActions>
                <Button
                  disabled={submitting}
                  onClick={() => {
                    createOpenActions.setFalse();
                    setSubmitError(null);
                  }}
                >
                  {t({ id: "cancelButton" })}
                </Button>

                <Button disabled={submitting} type="submit">
                  {t({ id: "saveButton" })}
                  {submitting && (
                    <CircularProgress
                      style={{ marginLeft: "10px" }}
                      color="inherit"
                      size={14}
                    />
                  )}
                </Button>
              </DialogActions>

              <ConfirmDialog
                dialogTitleKey="areYouSure"
                rawContent={
                  <>
                    {t(
                      { id: "addOrgAdminConfirmationText" },
                      { email: values.email }
                    )}
                    {descendantAdmins.length > 0 && (
                      <Alert
                        severity="warning"
                        sx={styles.descendantAdminsWarning}
                      >
                        {t(
                          { id: "adminExistsInDescendantOrganizations" },
                          {
                            email: values.email,
                            organizationNames: descendantAdmins
                              .map((a) => a.organizationName)
                              .join(", "),
                          }
                        )}
                      </Alert>
                    )}
                  </>
                }
                open={addConfirmOpen}
                onCancel={addConfirmOpenActions.setFalse}
                handleConfirm={onConfirm}
              />
            </form>
          )}
        />
      </Dialog>

      <ConfirmDialog
        dialogTitleKey="deleteAdministrator"
        contentKey="deleteAdministratorConfirmation"
        open={deleteAdminConfirmOpen}
        onCancel={deleteAdminConfirmOpenActions.setFalse}
        handleConfirm={deleteOrganizationAdmin}
        confirmLabel="confirmButton"
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default withErrorBoundary(OrganizationAdminUsers);
